'use client';

import React, { useEffect, useState, FC, useMemo } from 'react';
import { CardBody, Cards } from 'components/card';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import type {
  ChooseYourTier,
  ChooseYourTierProps,
} from '@/types/contractor/ChooseYourTierResponse';
import { FiCheck } from 'react-icons/fi';
import { FiPlus } from 'react-icons/fi';
import { MdClose } from 'react-icons/md';
import Button from 'components/common/Button';
import ModalComponent from 'components/modal/ModalComponent';
import Heading from 'components/common/Heading';
import { RenderHTML } from '@/components/renderHtml';

function decodeUnicode(str: string | null | undefined): string {
  if (!str) return '';
  return str.replace(/\\u([0-9a-fA-F]{4})/g, (_, code) =>
    String.fromCharCode(parseInt(code, 16))
  );
}

function renderBlocks(blocks: any[]) {
  return blocks.map((block, i) => {
    if (block.type === 'paragraph') {
      return (
        <p key={i}>
          {block.children?.map((child: any, j: number) => child.text).join('')}
        </p>
      );
    }
    return null;
  });
}

const ChooseYourTier: FC<ChooseYourTierProps> = ({ content }) => {
  const { name, description, backgroundColor, modalFooter } = content;
  const categories = useMemo(() => content.tiers || [], [content.tiers]);

  const [tabActive, setTabActive] = useState(categories[0]?.name || '');
  const [categoryData, setCategoryData] = useState<ChooseYourTier | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (!categories || categories.length === 0) {
      setCategoryData(null);
      return;
    }
    const selectedCategory = categories.find((data) => data.name === tabActive);
    setCategoryData(selectedCategory || null);
  }, [tabActive, categories]);

  useEffect(() => {
    const nav = document.querySelector('nav');
    if (nav) {
      if (isModalOpen) {
        nav.style.display = 'none';
      } else {
        nav.style.display = '';
      }
    }
    return () => {
      if (nav) {
        nav.style.display = '';
      }
    };
  }, [isModalOpen]);

  return (
    <section
      className='tier-section py-14 lg:py-24'
      style={{ backgroundColor: backgroundColor }}
    >
      <div className='tier-container mx-auto w-full max-w-[90rem] px-5 lg:px-24 xl:px-[7.5rem]'>
        <div className='tier-content flex w-full flex-col gap-12'>
          <h1 className='tier-title text-center text-4xl font-bold text-white sm:text-6xl'>
            {name}
          </h1>
          <div className='tier-description text-center text-xl text-gray-300 sm:text-2xl'>
            {Array.isArray(description) ? (
              renderBlocks(description)
            ) : (
              <RenderHTML data={description} />
            )}
          </div>

          <nav className='tier-navigation hide-scrollbar relative -mb-0.5 flex w-full justify-center'>
            <div className='tier-tab-container flex w-full max-w-[90rem] overflow-x-auto border-y border-[#273653] p-1 sm:justify-between sm:rounded-full sm:border'>
              <div className='flex w-full justify-between'>
                {categories?.map((data) => (
                  <button
                    key={data.id}
                    type='button'
                    className={`
                      tier-tab-button
                      ${
                        data.name === tabActive
                          ? 'bg-[#273653] text-white'
                          : 'text-white hover:bg-[#273653]/50'
                      }
                      inline-flex flex-1 items-center justify-center gap-x-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-all 
                      duration-200 focus:outline-none
                      disabled:pointer-events-none
                      disabled:opacity-50 sm:text-base
                    `}
                    onClick={() => setTabActive(data.name)}
                  >
                    {data.name}
                  </button>
                ))}
              </div>
            </div>
          </nav>
          {categoryData && (
            <div
              className='tier-card grid h-auto w-full grid-cols-1 overflow-hidden rounded-3xl sm:grid-cols-2'
              style={{
                backgroundColor: categoryData.backgroundColor,
              }}
            >
              <div className='tier-card-content order-2 flex w-full flex-col gap-3 p-9 font-light sm:order-1 lg:p-14'>
                {Array.isArray(categoryData.headings) &&
                  categoryData.headings.map((heading) => (
                    <div
                      key={heading.order}
                      className='tier-feature flex flex-col'
                    >
                      <h3 className='tier-feature-title flex items-start gap-2 text-lg font-bold'>
                        <FiCheck className='tier-feature-icon h-6 w-6 shrink-0 text-gray-800' />
                        {decodeUnicode(heading.text)}
                      </h3>
                      {categoryData.subheadings?.find(
                        (subheading) => subheading.order === heading.order
                      ) && (
                        <p className='tier-feature-description whitespace-pre-line pl-8 text-lg'>
                          {decodeUnicode(
                            categoryData.subheadings.find(
                              (subheading) => subheading.order === heading.order
                            )?.text || ''
                          )}
                        </p>
                      )}
                    </div>
                  ))}

                {Array.isArray(categoryData.subheadings) &&
                  categoryData.subheadings.map((subheading) => {
                    if (
                      !Array.isArray(categoryData.headings) ||
                      !categoryData.headings.some(
                        (heading) => heading.order === subheading.order
                      )
                    ) {
                      return (
                        <p
                          key={subheading.order}
                          className='tier-standalone-description whitespace-pre-line pl-8 text-lg'
                        >
                          {decodeUnicode(subheading.text)}
                        </p>
                      );
                    }
                    return null;
                  })}

                {categoryData.note && (
                  <p className='tier-note text-lg'>
                    {decodeUnicode(categoryData.note)}
                  </p>
                )}

                <div className='tier-pricing mt-8'>
                  <div className='tier-volume text-lg'>
                    {decodeUnicode(categoryData?.volume || '')}
                  </div>
                  <div className='tier-price mt-2 text-4xl font-bold leading-none sm:text-[80px]'>
                    {decodeUnicode(categoryData?.price || '')}
                  </div>
                </div>
              </div>

              <Cards className='tier-image-container relative order-1 mb-0 w-full sm:order-2'>
                <CardBody className='tier-image-wrapper relative my-auto flex h-full w-full flex-col items-start overflow-hidden pl-6 pt-10 sm:items-end sm:pr-14 sm:pt-14'>
                  {categoryData?.image && (
                    <img
                      className='tier-image mb-0 w-full max-w-[118px] object-contain duration-300 sm:max-w-[240px]'
                      src={categoryData.image.data?.attributes?.url}
                      alt={
                        categoryData.image.data?.attributes?.alternativeText ??
                        ''
                      }
                      loading='lazy'
                    />
                  )}
                </CardBody>
              </Cards>

              <div className='order-3  col-span-full pb-10'>
                {['Gold Tier', 'Platinum Tier', 'Diamond Tier'].includes(
                  categoryData.name
                ) && (
                  <div className='tier-action flex justify-start pl-8 sm:justify-end sm:pr-10'>
                    <Button
                      variant='contained'
                      color='gray-dark'
                      className='tier-info-button flex items-center gap-2'
                      onClick={() => setIsModalOpen(true)}
                    >
                      For more information <FiPlus className='h-4 w-4' />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <ModalComponent
        isOpen={isModalOpen}
        close={() => setIsModalOpen(false)}
        size='2xl'
        className='rounded-xl'
        style={{ zIndex: 9999 }}
        containerClass='z-[9999]'
      >
        <div className='px-6 pt-6'>
          <div className='flex justify-end'>
            <button onClick={() => setIsModalOpen(false)} className='p-2'>
              <MdClose className='size-7' />
            </button>
          </div>
        </div>
        <div className='px-6 py-4 lg:px-20'>
          <div className='flex flex-col gap-6'>
            {/* Tier Logo and Price Section */}
            <div className='flex flex-col items-center gap-4'>
              <img
                src={
                  categoryData?.modalContent?.[0]?.logo?.data?.attributes?.url
                }
                alt={
                  categoryData?.modalContent?.[0]?.logo?.data?.attributes
                    ?.alternativeText
                }
                className='h-auto w-[118px]'
              />
              <div className='pt-5 text-center'>
                <p className='text-lg'>
                  {decodeUnicode(categoryData?.modalContent?.[0]?.volume || '')}
                </p>
                <p className='pt-4 text-6xl font-bold'>
                  {decodeUnicode(categoryData?.modalContent?.[0]?.price || '')}
                </p>
              </div>
            </div>

            <div className='flex flex-col gap-5 pt-5'>
              <div>
                <Heading size={4}>Benefit details</Heading>
              </div>
              <hr className='border-gray-200' />

              {Array.isArray(categoryData?.modalContent?.[0]?.benefits) &&
                categoryData.modalContent[0].benefits.map((benefit, index) => (
                  <div key={index} className='benefit-item flex flex-col gap-4'>
                    {index > 0 && <hr className='border-gray-200' />}
                    <div className='benefit-content grid grid-cols-1 gap-4 pb-4 lg:grid-cols-12'>
                      <h3 className='benefit-title text-lg font-bold lg:col-span-3'>
                        {decodeUnicode(benefit.title)}
                      </h3>
                      <div
                        className={`benefit-description ${
                          benefit.image && benefit.image.data?.attributes?.url
                            ? 'lg:col-span-5'
                            : 'lg:col-span-9'
                        }`}
                      >
                        {Array.isArray(benefit.description) ? (
                          renderBlocks(benefit.description)
                        ) : (
                          <p className='text-lg'>
                            {decodeUnicode(benefit.description || '')}
                          </p>
                        )}
                      </div>
                      {benefit.image && benefit.image.data?.attributes?.url && (
                        <div className='benefit-image-container lg:col-span-4'>
                          <img
                            src={benefit.image.data.attributes.url}
                            alt={benefit.image.data.attributes.alternativeText}
                            className='benefit-image h-[196px] w-[262px] object-contain pb-4 pt-4'
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
            </div>

            <p className='text-center text-lg'>{decodeUnicode(modalFooter)}</p>
          </div>
        </div>
      </ModalComponent>
    </section>
  );
};

export default ChooseYourTier;
